import { useRef, useState } from "react";
import { UploadSimple, Camera, VideoCamera, X, ArrowClockwise, WarningCircle } from "@phosphor-icons/react";
import { Progress } from "@/components/ui/progress";
import { fmtSize, isMediaFile } from "./serviceApi";

// Drop zone + queue display. Parent owns the queue and runs uploads.
export function MediaDropzone({ disabled, disabledReason, onFiles, testId = "media-dropzone" }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const take = (list) => {
    const files = Array.from(list || []).filter(isMediaFile);
    if (files.length) onFiles(files);
  };
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (!disabled) take(e.dataTransfer.files); }}
      onClick={() => !disabled && inputRef.current?.click()}
      className={`rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors ${disabled ? "border-slate-200 bg-slate-50 cursor-not-allowed" : over ? "border-[#327de3] bg-[#eaf2ff] cursor-copy" : "border-slate-300 hover:border-[#327de3] cursor-pointer"}`}
      data-testid={testId}
    >
      <input ref={inputRef} type="file" multiple accept="image/*,video/*,.heic,.heif,.mov,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx,.zip" className="hidden" onChange={(e) => { take(e.target.files); e.target.value = ""; }} data-testid={`${testId}-input`} />
      {disabled ? (
        <div className="text-sm text-amber-700 inline-flex items-center gap-1.5" data-testid={`${testId}-disabled`}>
          <WarningCircle size={16} /> {disabledReason}
        </div>
      ) : (
        <>
          <UploadSimple size={22} className="mx-auto text-[#327de3] mb-1" />
          <div className="text-sm font-semibold text-slate-700">Drop photos & videos or click to browse</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Photos are stamped with the address, capture time and category. MP4 / MOV videos play in the browser. PDFs &amp; documents are attached as files. Max 2 GB per file; interrupted uploads resume.</div>
        </>
      )}
    </div>
  );
}

export function UploadQueue({ items, onRemove, onRetry }) {
  if (!items.length) return null;
  return (
    <div className="space-y-1.5 mt-2" data-testid="upload-queue">
      {items.map((it) => (
        <div key={it.key} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-md border border-slate-200" data-testid={`upload-item-${it.key}`}>
          {it.file.type.startsWith("video") || /\.mov$/i.test(it.file.name) ? <VideoCamera size={15} className="text-slate-500" /> : <Camera size={15} className="text-slate-500" />}
          <div className="flex-1 min-w-0">
            <div className="truncate text-slate-700">{it.file.name} <span className="text-[11px] text-slate-400">· {fmtSize(it.file.size)}</span></div>
            {(it.status === "uploading" || it.status === "processing") && <Progress value={it.progress} className="h-1.5 mt-1" />}
            {it.status === "error" && <div className="text-[11px] text-rose-600">{it.error}</div>}
          </div>
          <span className={`text-[10px] font-bold uppercase ${it.status === "done" ? "text-emerald-600" : it.status === "error" ? "text-rose-600" : "text-slate-400"}`} data-testid={`upload-status-${it.key}`}>
            {it.status === "uploading" ? `${it.progress}%` : it.status === "processing" ? "finalizing" : it.status === "done" ? "complete" : it.status}
          </span>
          {it.status === "error" && onRetry && (
            <button type="button" onClick={() => onRetry(it)} className="p-1 rounded hover:bg-slate-100 text-[#327de3]" title="Retry" data-testid={`upload-retry-${it.key}`}><ArrowClockwise size={13} /></button>
          )}
          {(it.status === "queued" || it.status === "error") && onRemove && (
            <button type="button" onClick={() => onRemove(it)} className="p-1 rounded hover:bg-slate-100 text-slate-400" title="Remove" data-testid={`upload-remove-${it.key}`}><X size={13} /></button>
          )}
        </div>
      ))}
    </div>
  );
}
